import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Trophy,
  Edit,
  UserPlus,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { formatGameDateTime, formatDeadlineDateTime } from '@/utils/timezone';
import { toast } from 'sonner';
import { useUserRole } from '@/hooks/useUserRole';
import { GameRosterModal } from '@/components/games/GameRosterModal';
import { VisualRosterDisplay } from '@/components/games/VisualRosterDisplay';
import { GameEditForm } from '@/components/forms/GameEditForm';
import { useGameAuthorization } from '@/hooks/useGameAuthorization';
import { gameManagementService } from '@/services/gameManagementService';
import { venueGamesService } from '@/services/venueGamesService';

interface GameData {
  id: string;
  game_name?: string;
  game_title?: string;
  game_description?: string;
  game_date: string;
  game_time: string;
  game_code?: string;
  game_status: string;
  status?: string;
  game_slug?: string;
  field_location?: string;
  venue_address?: string;
  venue_name?: string;
  venue_id?: string;
  home_team_id?: number;
  away_team_id?: number;
  home_team_status?: string;
  away_team_status?: string;
  roster_deadline?: string;
  readiness_deadline?: string;
  scorekeeper_needed?: boolean;
  scorekeeper_points?: number;
  home_team?: {
    team_name: string;
    created_by: string;
  } | null;
  away_team?: {
    team_name: string;
    created_by: string;
  } | null;
  // Allow any other properties from different game tables
  [key: string]: any;
}

const GameDetail: React.FC = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const { userId, permissions } = useUserRole();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [selectedTeamSide, setSelectedTeamSide] = useState<'home' | 'away'>('home');
  const [selectedTeamId, setSelectedTeamId] = useState<number>(0);
  const [selectedTeamName, setSelectedTeamName] = useState<string>('');
  const [showEditForm, setShowEditForm] = useState(false);
  const [isVenueGame, setIsVenueGame] = useState(false);
  
  // Get authorization permissions
  const { permissions: authPermissions, loading: authLoading } = useGameAuthorization({
    gameData: game,
    gameId: game?.id || ''
  });

  useEffect(() => {
    if (gameSlug) {
      loadGameData();
    }
  }, [gameSlug]);

  const loadGameData = async () => {
    try {
      // First try to find by slug in games table
      let { data, error }: { data: any, error: any } = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_name, created_by),
          away_team:teams!games_away_team_id_fkey(team_name, created_by),
          sport:available_sports(sport_name)
        `)
        .eq('game_slug', gameSlug)
        .single();

      // If not found in games table, try venue_games table with ID (fallback for old URLs)
      if (error && error.code === 'PGRST116') {
        const response = await supabase
          .from('venue_games')
          .select(`
            *,
            home_team:teams!venue_games_home_team_id_fkey(team_name, created_by),
            away_team:teams!venue_games_away_team_id_fkey(team_name, created_by),
            sport:available_sports(sport_name)
          `)
          .eq('id', gameSlug)
          .single();
        data = response.data as GameData;
        error = response.error;
        setIsVenueGame(true);
      }

      if (error) throw error;
      setGame(data as GameData);
    } catch (error) {
      console.error('Error loading game:', error);
      toast.error('Failed to load game details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'confirmed': return 'default';
      case 'pending': return 'secondary';
      case 'approved': return 'outline';
      case 'rejected': return 'destructive';
      default: return 'secondary';
    }
  };

  const getTeamStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'accepted': return 'default';
      case 'pending': return 'secondary';
      case 'assigned': return 'outline';
      default: return 'secondary';
    }
  };

  const handleManageRoster = (teamId: number, teamName: string, teamSide: 'home' | 'away') => {
    setSelectedTeamId(teamId);
    setSelectedTeamName(teamName);
    setSelectedTeamSide(teamSide);
    setShowRosterModal(true);
  };

  const getUserTeamSide = (): 'home' | 'away' | null => {
    if (!game || !userId) return null;
    
    if (game.home_team?.created_by === userId) return 'home';
    if (game.away_team?.created_by === userId) return 'away';
    return null;
  };

  const handleSaveGame = async (updatedData: any) => {
    if (!game) return;
    
    try {
      if (isVenueGame) {
        await venueGamesService.updateVenueGame(game.id, updatedData);
      } else {
        await gameManagementService.updateGame(game.id, updatedData);
      }
      
      // Reload game data
      await loadGameData();
      setShowEditForm(false);
      toast.success('Game updated successfully');
      
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error('Failed to save game changes');
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Game Not Found</h2>
          <Button onClick={() => navigate('/game-management')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Game Management
          </Button>
        </div>
      </div>
    );
  }

  const gameTitle = game.game_title || game.game_name || 'Game';
  const gameDescription = game.game_description || 'No description provided';
  const location = game.field_location || game.venue_address || game.venue_name || 'TBD';

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="pt-20"> {/* Add padding for fixed header */}
        <div className="container mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/game-management')}
            className="h-10 w-10 p-0 border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">{gameTitle}</h1>
            <p className="text-gray-400">Game Code: {game.game_code || 'N/A'}</p>
          </div>
        </div>

        {/* Game Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Game Status</p>
                  <Badge variant={getStatusBadgeVariant(game.game_status)} className="mt-1">
                    {game.game_status}
                  </Badge>
                </div>
                <Trophy className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Date & Time</p>
                  {(() => {
                    const { date, timeWithZone } = formatGameDateTime(
                      game.game_date, 
                      game.game_time, 
                      'UTC' // TODO: Use actual game timezone from DB
                    );
                    return (
                      <>
                        <p className="font-medium text-white">{date}</p>
                        <p className="text-sm text-gray-300">{timeWithZone}</p>
                      </>
                    );
                  })()}
                </div>
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Location</p>
                  <p className="font-medium text-white">{location}</p>
                </div>
                <MapPin className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-gray-800">
            <TabsTrigger value="overview" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="roster" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">Roster Management</TabsTrigger>
            <TabsTrigger value="settings" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">Game Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Game Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2 text-white">Description</h4>
                  <p className="text-gray-400">{gameDescription}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3 text-white">Home Team</h4>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-sports-green/10 rounded-lg">
                         <Users className="h-4 w-4 text-sports-green" />
                       </div>
                       <div>
                         <p className="font-medium text-white">{game.home_team?.team_name || 'TBD'}</p>
                         <Badge variant={getTeamStatusBadgeVariant(game.home_team_status || 'pending')} className="text-xs">
                           {game.home_team_status || 'pending'}
                         </Badge>
                       </div>
                    </div>
                  </div>

                   <div>
                     <h4 className="font-medium mb-3 text-white">Away Team</h4>
                     <div className="flex items-center gap-3">
                       <div className="p-2 bg-blue-500/10 rounded-lg">
                         <Users className="h-4 w-4 text-blue-400" />
                       </div>
                       <div>
                         <p className="font-medium text-white">{game.away_team?.team_name || 'TBD'}</p>
                         <Badge variant={getTeamStatusBadgeVariant(game.away_team_status || 'pending')} className="text-xs">
                           {game.away_team_status || 'pending'}
                         </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                   <div>
                     <h4 className="font-medium mb-2 text-white">Sport</h4>
                     <p className="text-sm text-gray-400">{game.sport?.sport_name || 'Not specified'}</p>
                   </div>
                   
                   <div>
                     <h4 className="font-medium mb-2 text-white">Roster Deadline</h4>
                    {game.roster_deadline ? (() => {
                      const { fullDateTime, isOverdue } = formatDeadlineDateTime(game.roster_deadline);
                      return (
                         <p className={`text-sm ${isOverdue ? 'text-red-400 font-medium' : 'text-gray-400'}`}>
                           {fullDateTime}
                           {isOverdue && ' (OVERDUE)'}
                        </p>
                      );
                    })() : (
                      <p className="text-sm text-gray-400">Not set</p>
                    )}
                  </div>

                  <div>
                    <h4 className="font-medium mb-2 text-white">Scorekeeper</h4>
                    <div className="flex items-center gap-2">
                      {game.scorekeeper_needed ? (
                        <>
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                          <span className="text-gray-400">Needed ({game.scorekeeper_points || 0} points)</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-gray-400">Assigned</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roster" className="space-y-4">
            {/* Visual Roster Display */}
            <VisualRosterDisplay
              gameId={game.id}
              homeTeam={{
                team_id: game.home_team_id || 0,
                team_name: game.home_team?.team_name || 'Home Team',
                roster: []
              }}
              awayTeam={{
                team_id: game.away_team_id || 0,
                team_name: game.away_team?.team_name || 'Away Team',
                roster: []
              }}
              currentUserId={userId || ''}
              userRole={permissions.role}
              userTeamSide={getUserTeamSide()}
              showBothTeams={['venue', 'admin'].includes(permissions.role)}
              canEdit={['coach', 'team-owner'].includes(permissions.role) || ['venue', 'admin'].includes(permissions.role)}
            />

            {/* Traditional Roster Management */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team Roster Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h3 className="font-medium mb-2 text-white">Game Roster Management</h3>
                    <p className="text-gray-400 text-sm">
                      Manage team rosters for this game. Select players, coaches, and support staff who will participate.
                    </p>
                  </div>
                  
                  {game.home_team_id && (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-700 rounded-lg">
                      <div>
                        <h4 className="font-semibold text-white">{game.home_team?.team_name || 'Home Team'}</h4>
                        <p className="text-sm text-gray-400">Home team roster</p>
                      </div>
                      <Button
                        onClick={() => handleManageRoster(game.home_team_id!, game.home_team?.team_name || 'Home Team', 'home')}
                        className="bg-sports-green text-black hover:bg-green-600 mt-2 sm:mt-0"
                      >
                        Manage Roster
                      </Button>
                    </div>
                  )}
                  
                  {game.away_team_id && (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-700 rounded-lg">
                      <div>
                        <h4 className="font-semibold text-white">{game.away_team?.team_name || 'Away Team'}</h4>
                        <p className="text-sm text-gray-400">Away team roster</p>
                      </div>
                      <Button
                        onClick={() => handleManageRoster(game.away_team_id!, game.away_team?.team_name || 'Away Team', 'away')}
                        className="bg-sports-green text-black hover:bg-green-600 mt-2 sm:mt-0"
                      >
                        Manage Roster
                      </Button>
                    </div>
                  )}
                  
                  {(!game.home_team_id && !game.away_team_id) && (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-400">No teams assigned to this game yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            {showEditForm ? (
              <GameEditForm
                gameData={game}
                onSave={handleSaveGame}
                onCancel={() => setShowEditForm(false)}
                userPermissions={authPermissions}
              />
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    Game Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Edit className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <h3 className="font-medium mb-2 text-white">Game Configuration</h3>
                    <p className="text-gray-400 mb-4">
                      Update game details, timing, and other settings.
                    </p>
                    
                    {authLoading ? (
                      <div className="animate-pulse">
                        <div className="h-10 bg-gray-600 rounded w-40 mx-auto"></div>
                      </div>
                    ) : authPermissions.canEditBasicInfo || authPermissions.canEditTeams || authPermissions.canEditSettings ? (
                      <Button 
                        variant="outline" 
                        onClick={() => setShowEditForm(true)}
                        className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Game Details
                      </Button>
                    ) : (
                      <div className="text-gray-400">
                        <p className="text-sm">You don't have permission to edit this game.</p>
                        <p className="text-xs mt-2">
                          Contact the game creator, venue manager, or team coach for assistance.
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Roster Management Modal */}
        {showRosterModal && game && selectedTeamId && (
          <GameRosterModal
            isOpen={showRosterModal}
            onClose={() => setShowRosterModal(false)}
            gameId={game.id}
            teamId={selectedTeamId}
            teamName={selectedTeamName}
            teamSide={selectedTeamSide}
            currentUserId={userId || ''}
            userRole={permissions.role}
            currentUserTeams={[]}
            sportName={game.sport?.sport_name}
          />
        )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default GameDetail;